import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import ProtectedRoute from '../../components/ProtectedRoute';
import Skeleton from '../../components/Skeleton';

function OrdersContent() {
  const { t } = useTranslation();
  const { showAlert } = useNotification();
  const { T, S } = useTheme();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const STATUS_COLORS = {
    pending:   { bg: 'rgba(234,179,8,0.15)',  color: '#eab308', label: t('admin.orderPending') },
    paid:      { bg: 'rgba(59,130,246,0.15)',  color: '#3b82f6', label: t('admin.orderPaid') },
    fulfilled: { bg: 'rgba(34,197,94,0.15)',   color: '#22c55e', label: t('admin.orderFulfilled') },
    cancelled: { bg: 'rgba(239,68,68,0.15)',   color: '#ef4444', label: t('admin.orderCancelled') },
  };

  const STATUSES = ['pending', 'paid', 'fulfilled', 'cancelled'];

  const fetchOrders = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await api.getAdminOrders(params);
      setOrders(data);
    } catch (err) {
      showAlert(err?.message || t('admin.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchOrders();
  }, [statusFilter]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.updateAdminOrder(orderId, { status: newStatus });
      fetchOrders();
    } catch (err) {
      showAlert(err?.message || t('admin.failedSave'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const badgeStyle = (status) => {
    const sc = STATUS_COLORS[status] || STATUS_COLORS.pending;
    return {
      background: sc.bg,
      color: sc.color,
      padding: '0.2rem 0.6rem',
      borderRadius: '4px',
      fontSize: '0.72rem',
      fontFamily: "'DM Sans', sans-serif",
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      display: 'inline-block',
    };
  };

  return (
    <AdminLayout role="admin">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={S.pageTitle}>{t('admin.allOrders')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange, marginBottom: '1.5rem' }} />

        {/* Status Filter */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ ...S.select, width: '200px', fontSize: '0.82rem', marginBottom: 0 }}
          >
            <option value="">{t('admin.allOrders')}</option>
            {STATUSES.map(s => (
              <option key={s} value={s}>{STATUS_COLORS[s]?.label || s}</option>
            ))}
          </select>

          {statusFilter && (
            <button
              onClick={() => setStatusFilter('')}
              style={{ ...S.btnOutline, padding: '0.4rem 0.8rem', fontSize: '0.75rem', height: '36px' }}
            >
              {t('admin.clearFilters')}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <Skeleton count={5} height="48px" />
      ) : orders.length === 0 ? (
        <p style={{ color: T.muted, fontStyle: 'italic' }}>{t('admin.noOrdersFound')}</p>
      ) : (
        <div style={{ ...S.card, padding: 0, overflow: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>ID</th>
                <th style={S.th}>Branch ID</th>
                <th style={S.th}>{t('admin.orderTotal')}</th>
                <th style={S.th}>{t('admin.orderStatus')}</th>
                <th style={S.th}>{t('admin.createdAt') || 'Created At'}</th>
                <th style={S.th}>{t('admin.actions') || 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id}>
                  <td style={S.td}>#{order.id}</td>
                  <td style={S.td}>{order.branch_id}</td>
                  <td style={S.td}>
                    <span style={{ color: T.orange, fontWeight: 600 }}>
                      {order.total_price?.toFixed(2)} EGP
                    </span>
                  </td>
                  <td style={S.td}>
                    <span style={badgeStyle(order.status)}>
                      {STATUS_COLORS[order.status]?.label || order.status}
                    </span>
                  </td>
                  <td style={S.td}>{formatDate(order.created_at)}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
                        value={order.status}
                        onChange={e => handleStatusChange(order.id, e.target.value)}
                        style={{
                          ...S.select,
                          width: 'auto',
                          fontSize: '0.75rem',
                          padding: '0.3rem 0.5rem',
                          marginBottom: 0,
                        }}
                      >
                        {STATUSES.map(s => (
                          <option key={s} value={s}>{STATUS_COLORS[s]?.label || s}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => setSelectedOrder(order)}
                        style={S.btnSmall}
                      >
                        {t('admin.orderDetails')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div style={S.modal} onClick={() => setSelectedOrder(null)}>
          <div style={{ ...S.modalBox, maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalTitle}>
              {t('admin.orderDetails')} #{selectedOrder.id}
            </h2>

            <div style={{ marginBottom: '1rem', fontSize: '0.88rem', color: T.muted }}>
              <p><strong style={{ color: T.white }}>User ID:</strong> {selectedOrder.user_id}</p>
              <p><strong style={{ color: T.white }}>Branch ID:</strong> {selectedOrder.branch_id}</p>
              <p><strong style={{ color: T.white }}>{t('admin.orderTotal')}:</strong>{' '}
                <span style={{ color: T.orange }}>{selectedOrder.total_price?.toFixed(2)} EGP</span>
              </p>
              <p><strong style={{ color: T.white }}>{t('admin.orderStatus')}:</strong>{' '}
                <span style={badgeStyle(selectedOrder.status)}>
                  {STATUS_COLORS[selectedOrder.status]?.label || selectedOrder.status}
                </span>
              </p>
              {selectedOrder.delivery_address && (
                <p><strong style={{ color: T.white }}>Delivery:</strong> {selectedOrder.delivery_address}</p>
              )}
              {selectedOrder.contact_phone && (
                <p><strong style={{ color: T.white }}>Phone:</strong> {selectedOrder.contact_phone}</p>
              )}
              <p><strong style={{ color: T.white }}>Created:</strong> {formatDate(selectedOrder.created_at)}</p>
            </div>

            <h3 style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.82rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: T.white,
              marginBottom: '0.75rem',
            }}>
              {t('admin.orderItems')}
            </h3>

            {selectedOrder.items && selectedOrder.items.length > 0 ? (
              <table style={{ ...S.table, fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, fontSize: '0.68rem' }}>Product ID</th>
                    <th style={{ ...S.th, fontSize: '0.68rem' }}>Qty</th>
                    <th style={{ ...S.th, fontSize: '0.68rem' }}>Unit Price</th>
                    <th style={{ ...S.th, fontSize: '0.68rem' }}>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items.map(item => (
                    <tr key={item.id}>
                      <td style={S.td}>{item.product_id}</td>
                      <td style={S.td}>{item.quantity}</td>
                      <td style={S.td}>{item.unit_price?.toFixed(2)} EGP</td>
                      <td style={S.td}>
                        <span style={{ color: T.orange }}>
                          {(item.quantity * item.unit_price).toFixed(2)} EGP
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: T.muted, fontStyle: 'italic', fontSize: '0.82rem' }}>No items.</p>
            )}

            <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
              <button onClick={() => setSelectedOrder(null)} style={S.btnOutline}>
                {t('common.close') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Orders() {
  return <ProtectedRoute requireSuperAdmin><OrdersContent /></ProtectedRoute>;
}
